import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import * as shop from '../api/shop';
import { useAuth } from '../auth/AuthContext';
import { CountsContext, type CountsApi } from './CountsContext';
import { hasNewReply } from '../lib/replies';
import { isFinished } from '../lib/requestState';

export function CountsProvider({ children }: { children: ReactNode }) {
  const { authenticated } = useAuth();
  const [cart, setCart] = useState<number | null>(null);
  const [requests, setRequests] = useState<number | null>(null);
  const [newReplies, setNewReplies] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [requestsTick, setRequestsTick] = useState(0);

  const refreshCart = useCallback(() => setTick((n) => n + 1), []);
  const refreshRequests = useCallback(() => setRequestsTick((n) => n + 1), []);

  useEffect(() => {
    if (!authenticated) {
      setCart(null);
      return;
    }

    const controller = new AbortController();

    shop
      .cart({ signal: controller.signal })
      .then((data) => setCart(data.items?.length ?? 0))
      /* Un contor care nu se poate citi ramane ascuns. Nu merita o eroare pe
         ecran: omul a venit sa faca altceva, iar cosul se vede oricum intreg
         cand intra pe el. */
      .catch(() => {});

    return () => controller.abort();
  }, [authenticated, tick]);

  /*
   * Numarul cererilor DESCHISE.
   *
   * Badge-ul de langa „Cererile mele" spune cate cereri mai asteapta ceva, nu
   * cate ai trimis vreodata: o cerere rezolvata sau inchisa nu mai cere nimic
   * de la om, iar un numar care doar creste nu mai e citit de nimeni. Ce e
   * „terminat" hotaraste `isFinished()` - acelasi loc pentru tot catalogul.
   */
  useEffect(() => {
    if (!authenticated) {
      setRequests(null);
      setNewReplies(null);
      return;
    }

    const controller = new AbortController();

    /*
     * Aceeasi lista, doua numere.
     *
     * Cererile deschise dau badge-ul; `total` din api nu ajunge, fiindca ruta
     * nu filtreaza dupa stare, deci se numara peste toata lista. Randurile dau
     * si clopotelul: fiecare poarta `last_staff_comment_id`, iar ce s-a vazut
     * se stie local.
     */
    shop
      .allRequests({ signal: controller.signal })
      .then((rows) => {
        setRequests(rows.filter((row) => !isFinished(row)).length);

        const unseen = rows.filter(
          (row) => 'last_staff_comment_id' in row && hasNewReply(row.id, row.last_staff_comment_id),
        ).length;

        setNewReplies(unseen);
      })
      /* Ca la cos: un contor care nu se poate citi ramane ascuns, nu striga. */
      .catch(() => {});

    return () => controller.abort();
  }, [authenticated, requestsTick]);

  const value = useMemo<CountsApi>(
    () => ({ cart, refreshCart, requests, refreshRequests, newReplies }),
    [cart, refreshCart, requests, refreshRequests, newReplies],
  );

  return <CountsContext value={value}>{children}</CountsContext>;
}
